import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getTests, getISRTPercentiles, getSprintPercentiles, getSquadProfiling } from '../../api';
import {
  MOBILITY_TESTS, STRENGTH_TESTS,
  lsiPct, lsiColor, hqRatio, hqColor, abAdRatio, abAdColor, getPercentileColor, cmjPeakPowerWatts,
  SPRINT_ISRT_METRIC,
} from '../physicalTestingShared';
import QuadrantMatrix from '../../components/QuadrantMatrix';

const TEST_TYPES = ['mobility', 'strength', 'sprint', 'cmj', 'hop', 'isrt', 'weight', 'skinfold', 'passing-drill', 'dropjump', 'dropjump_sl'];

function fmt(v, digits = 1) {
  return v === null || v === undefined || v === '' || isNaN(v) ? '—' : Number(v).toFixed(digits);
}

function LRRowDisplay({ label, left, right }) {
  const pct = lsiPct(left, right);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 70px', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-strong)', fontSize: 12 }}>
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div>{left ?? '—'}</div>
      <div>{right ?? '—'}</div>
      <div style={{ fontWeight: 700, color: lsiColor(pct), textAlign: 'center' }}>{pct !== null ? `${pct.toFixed(0)}%` : '—'}</div>
    </div>
  );
}

export default function PlayerPhysicalTests({ playerRecord }) {
  const [data, setData] = useState({});
  const [isrtPercentile, setIsrtPercentile] = useState(null);
  const [sprint10Percentile, setSprint10Percentile] = useState(null);
  const [sprint30Percentile, setSprint30Percentile] = useState(null);
  const [squadProfiling, setSquadProfiling] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerRecord?.id) { setLoading(false); return; }
    Promise.all(TEST_TYPES.map(t => getTests(t, playerRecord.id)))
      .then(results => {
        const d = {};
        TEST_TYPES.forEach((t, i) => { d[t] = results[i] || []; });
        setData(d);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    getISRTPercentiles()
      .then(rows => {
        const mine = (rows || []).find(r => String(r.player_id) === String(playerRecord.id));
        setIsrtPercentile(mine?.percentile ?? null);
      })
      .catch(() => {});
    getSprintPercentiles()
      .then(rows => {
        const mine = (rows || []).find(r => String(r.player_id) === String(playerRecord.id));
        setSprint10Percentile(mine?.percentile_10m ?? null);
        setSprint30Percentile(mine?.percentile_30m ?? null);
      })
      .catch(() => {});
    // Squad Profiling always computes its crosshairs/bands from the full squad
    // dataset, never a subset — same data the staff Squad Profiling tab uses.
    getSquadProfiling().then(rows => setSquadProfiling(rows || [])).catch(() => {});
  }, [playerRecord?.id]);

  const latestMobility = data.mobility?.[0] || {};
  const latestStrength = data.strength?.[0] || {};
  const latestSprint    = data.sprint?.[0] || {};
  const latestCmj       = data.cmj?.[0] || {};
  const latestHop       = data.hop?.[0] || {};
  const latestIsrt      = data.isrt?.[0] || {};
  const latestWeight    = data.weight?.[0] || {};
  const latestSkinfold  = data.skinfold?.[0] || {};
  const latestDropjump   = data.dropjump?.[0] || {};
  const latestDropjumpSL = data.dropjump_sl?.[0] || {};
  const hq   = hqRatio(latestStrength);
  const abad = abAdRatio(latestStrength);

  const trendData = (rows, key) => [...(rows || [])].reverse().map(r => ({
    date: (r.test_date || '').slice(5, 10),
    value: r[key] != null ? Number(r[key]) : null,
  }));

  const CHARTS = useMemo(() => [
    { title: 'Weight (kg)',                   data: trendData(data.weight, 'weight_kg'),                      color: '#3b82f6' },
    { title: 'Sum-8 Skinfold',                data: trendData(data.skinfold, 'sum_8'),                        color: '#f59e0b' },
    { title: 'CMJ Height (cm)',               data: trendData(data.cmj, 'height_cm'),                         color: '#10b981' },
    { title: 'CMJ Peak Power (W/kg)',         data: trendData(data.cmj, 'peak_power_wkg'),                    color: '#ef4444' },
    { title: 'ISRT MAX Stage',                data: trendData(data.isrt, 'final_stage'),                      color: '#8b5cf6' },
    { title: 'Adjusted Passing Drill %HRmax', data: trendData(data['passing-drill'], 'adjusted_pct_hrmax'),   color: '#06b6d4', detail: true },
  ], [data]);

  const cardStyle = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 14 };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div style={{ padding: '14px 14px 28px' }}>
      <h1 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Physical Tests</h1>

      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { label: 'Sprint 10m', val: fmt(latestSprint.sprint_10m, 2), unit: 's', percentile: sprint10Percentile },
            { label: 'Sprint 30m', val: fmt(latestSprint.sprint_30m, 2), unit: 's', percentile: sprint30Percentile },
            { label: 'CMJ Height', val: fmt(latestCmj.height_cm), unit: 'cm' },
            { label: 'CMJ Peak Power', val: fmt(latestCmj.peak_power_wkg, 1), unit: 'W/kg' },
            { label: 'CMJ Peak Power', val: fmt(cmjPeakPowerWatts(latestCmj.peak_power_wkg, playerRecord?.weight), 0), unit: 'W' },
            { label: 'Hop RSI', val: fmt(latestHop.mean_rsi, 2), unit: '' },
            { label: 'Drop Jump RSI', val: fmt(latestDropjump.rsi, 2), unit: '' },
            { label: 'Drop Jump Peak Power', val: fmt(latestDropjump.peak_power_wkg, 1), unit: 'W/kg' },
            { label: 'ISRT Stage', val: fmt(latestIsrt.final_stage, 0), unit: '', percentile: isrtPercentile },
            { label: 'Weight', val: fmt(latestWeight.weight_kg), unit: 'kg' },
            { label: 'Sum-8 Skinfold', val: fmt(latestSkinfold.sum_8), unit: '' },
          ].map(({ label, val, unit, percentile }, i) => (
            <div key={`${label}-${i}`} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {val}{val !== '—' ? unit : ''}
                {percentile != null && (
                  <span style={{ color: getPercentileColor(percentile), marginLeft: 6 }}>{percentile}%</span>
                )}
              </div>
            </div>
          ))}
        </div>
        {(isrtPercentile != null || sprint10Percentile != null || sprint30Percentile != null) && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>
            The % next to ISRT Stage and Sprint times shows how your most recent result compares to the rest of the squad — 100% means the best (or fastest) in the team.
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Squad Profiling — Power</h3>
        <QuadrantMatrix mode="player" playerId={playerRecord?.id} data={squadProfiling} />
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Squad Profiling — Acceleration & Endurance Capacity</h3>
        <QuadrantMatrix mode="player" playerId={playerRecord?.id} data={squadProfiling} metric={SPRINT_ISRT_METRIC} />
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Mobility {latestMobility.test_date ? `(${latestMobility.test_date.slice(0, 10)})` : ''}</h3>
        {MOBILITY_TESTS.map(t => (
          <LRRowDisplay key={t.key} label={t.label} left={latestMobility[`${t.key}_left`]} right={latestMobility[`${t.key}_right`]} />
        ))}
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Strength {latestStrength.test_date ? `(${latestStrength.test_date.slice(0, 10)})` : ''}</h3>
        {STRENGTH_TESTS.map(t => (
          <LRRowDisplay key={t.key} label={t.label} left={latestStrength[`${t.key}_left`]} right={latestStrength[`${t.key}_right`]} />
        ))}
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 8, padding: 10, textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Ratio H:Q</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: hqColor(hq) }}>{hq !== null ? hq.toFixed(2) : '—'}</div>
          </div>
          <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 8, padding: 10, textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Ratio AB:AD</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: abAdColor(abad) }}>{abad !== null ? `${abad.toFixed(0)}%` : '—'}</div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Single Leg Drop Jump {latestDropjumpSL.test_date ? `(${latestDropjumpSL.test_date.slice(0, 10)})` : ''}</h3>
        <LRRowDisplay label="RSI" left={latestDropjumpSL.rsi_left} right={latestDropjumpSL.rsi_right} />
        <LRRowDisplay label="Peak Power (W/kg)" left={latestDropjumpSL.peak_power_wkg_left} right={latestDropjumpSL.peak_power_wkg_right} />
      </div>

      {CHARTS.map(chart => (
        <div key={chart.title} style={cardStyle}>
          <h4 style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{chart.title}</h4>
          {chart.data.length > 0 ? (
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={chart.data}>
                <XAxis dataKey="date" stroke="var(--text-dimmed)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                <YAxis stroke="var(--text-dimmed)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 8, fontSize: 12 }} formatter={chart.detail ? (v => Number(v).toFixed(1)) : undefined} />
                <Line type="monotone" dataKey="value" stroke={chart.color} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dimmed)', fontSize: 12 }}>No data yet</div>
          )}
        </div>
      ))}
    </div>
  );
}
